/* eslint-disable camelcase */
import { useCallback, useEffect, useRef } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { getCurrentSessionURL } from "@fullstory/browser";
import { isEmpty } from "lodash";

import { useGetManagerProfile } from "manager/hooks/api";
import { useActiveUser } from "shared/hooks/api";
import { getZendeskToken } from "shared/lib/api/zendeskApi";
import { isManagerApp, useClaims } from "shared/utils/auth";

const contextKey = (companyId: unknown, role: unknown) =>
  `${companyId ?? ""}|${role ?? ""}`;

const useZendesk = () => {
  const { REACT_APP_ZENDESK_KEY: ZENDESK_KEY } = process.env;

  const { claims, company } = useClaims();
  const { activeUser } = useActiveUser();
  const { data: profile, isFetched: isProfileFetched } = useGetManagerProfile({
    enabled: !isEmpty(company) && !isEmpty(activeUser) && isManagerApp(),
  });

  const tokenRequestRef = useRef<Record<string, unknown> | null>(null);
  // Identity (email/username) that loginUser last ran for; a different one is a real identity change.
  const loggedInForRef = useRef<string | null>(null);
  // Company/role context the backend last saw; null until the first login has run.
  const sentContextRef = useRef<string | null>(null);

  // Identity and login_at come from claims. A late company is synced to Zendesk in the
  // background instead of repeating loginUser. Only the FIRST login of a manager with a
  // company waits for the profile, so that first token request carries their role (the
  // backend defaults a missing role to "applicant" and would overwrite a manager's role
  // with it). It must not gate payloadReady: a company arriving after login would flip it
  // false, reset the login guard below, and cause a second loginUser. The profile query
  // only runs with a company, so managers without one are not held up.
  const identity = claims?.email || claims?.username;
  const payloadReady =
    Boolean(identity) &&
    claims != null &&
    (typeof claims?.iat === "number" || typeof claims?.origIat === "number");
  const waitingForManagerProfile =
    isManagerApp() && !isEmpty(company) && !isProfileFetched;

  useEffect(() => {
    if (!payloadReady) {
      tokenRequestRef.current = null;
      loggedInForRef.current = null;
      sentContextRef.current = null;
      return;
    }
    // Keep the previous body while a manager's profile loads: a body with a company but no
    // role would make the backend default the role to "applicant".
    if (waitingForManagerProfile) return;

    tokenRequestRef.current = {
      company: company?.slug,
      companyName: company?.name,
      companyId: company?.id,
      role: profile?.role,
      login_at: claims?.iat ?? claims?.origIat,
      latest_fullstory_session: getCurrentSessionURL?.() ?? undefined,
    };
  }, [
    payloadReady,
    company?.slug,
    company?.name,
    company?.id,
    profile?.role,
    claims?.iat,
    claims?.origIat,
    waitingForManagerProfile,
  ]);

  const fetchZendeskJwt = useCallback(async function fetchLatestZendeskJwt() {
    const body = tokenRequestRef.current;
    if (!body) return null;

    sentContextRef.current = contextKey(body.companyId, body.role);
    try {
      const response = (await getZendeskToken(body)) as {
        zendesk?: { jwt?: string; token?: string };
      } | null;
      if (body !== tokenRequestRef.current) return fetchLatestZendeskJwt();
      return response?.zendesk?.jwt ?? response?.zendesk?.token ?? null;
    } catch (error) {
      console.error("Zendesk token fetch failed", error);
      datadogRum.addError(error);
      if (body !== tokenRequestRef.current) return fetchLatestZendeskJwt();
      return null;
    }
  }, []);

  const performLogin = useCallback(() => {
    if (!payloadReady || typeof window.zE !== "function") return;
    if (loggedInForRef.current === identity) return;
    // First login only: once logged in, a late company never reaches this point again.
    if (waitingForManagerProfile) return;
    loggedInForRef.current = identity as string;

    const run = () => {
      sentContextRef.current = contextKey(
        tokenRequestRef.current?.companyId,
        tokenRequestRef.current?.role
      );
      window.zE(
        "messenger",
        "loginUser",
        (callback: (token: string) => void) => {
          fetchZendeskJwt().then((token) => {
            // Always invoke Zendesk's callback; omitting it on fetch failure can leave the widget stuck.
            callback(token || "");
          });
        },
        (error: unknown) => {
          if (error) {
            console.error("Zendesk loginUser failed", error);
            datadogRum.addError(error as Error);
          }
        }
      );
    };

    window.zE(run);
  }, [payloadReady, identity, waitingForManagerProfile, fetchZendeskJwt]);

  // Read latest performLogin when payloadReady flips after the snippet is already on the page.
  const performLoginRef = useRef(performLogin);
  performLoginRef.current = performLogin;

  useEffect(() => {
    if (!ZENDESK_KEY) return;

    const runLatestLogin = () => performLoginRef.current();

    if (document.getElementById("ze-snippet")) {
      runLatestLogin();
      return;
    }

    const script = document.createElement("script");
    script.id = "ze-snippet";
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_KEY}`;
    script.async = true;
    script.onload = () => setTimeout(runLatestLogin, 100);
    document.body.appendChild(script);
  }, [ZENDESK_KEY, performLogin]);

  // Company or role arriving after login only needs the backend to emit its sync event;
  // the JWT is ignored and loginUser is not called again.
  useEffect(() => {
    if (!payloadReady || sentContextRef.current === null) return;
    // Wait for the profile so the role goes out with the company; sending now would also
    // mark the new context as sent and suppress the later request that has the role.
    if (waitingForManagerProfile) return;

    const body = tokenRequestRef.current;
    const nextContext = contextKey(company?.id, profile?.role);
    if (!body || nextContext === sentContextRef.current) return;

    sentContextRef.current = nextContext;
    getZendeskToken(body).catch((error: unknown) => {
      console.error("Zendesk context sync failed", error);
      datadogRum.addError(error as Error);
    });
  }, [payloadReady, waitingForManagerProfile, company?.id, profile?.role]);

  const zE = (...args: unknown[]) => {
    // @ts-ignore
    return window?.zE?.(...args);
  };

  return {
    open: () => zE("messenger", "open"),
    close: () => zE("messenger", "close"),
    show: () => zE("messenger", "show"),
    hide: () => zE("messenger", "hide"),
  };
};

export const Zendesk = () => {
  useZendesk();
  return null;
};
